import { Head, Link, router } from "@inertiajs/react"
import { Edit, Plus, Trash2, UserPlus } from "lucide-react"

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "@/components/ui/empty"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import AppLayout from "@/layouts/app-layout"
import { adminPropertiesPath } from "@/routes"
import type { BreadcrumbItem, LeasingContact, Property } from "@/types"

interface Props {
  property: Property
  leasing_contacts: LeasingContact[]
}

export default function Index({ property, leasing_contacts }: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Properties", href: adminPropertiesPath() },
    { title: property.name, href: `/admin/properties/${property.id}` },
    { title: "Leasing Contacts", href: `/admin/properties/${property.id}/leasing_contacts` },
  ]

  const handleDelete = (id: number) => {
    if (confirm("Remove this leasing contact?")) {
      router.delete(`/admin/properties/${property.id}/leasing_contacts/${id}`)
    }
  }

  const handleConvert = (c: LeasingContact) => {
    const email = window.prompt(
      `Convert "${c.name}" into an agent login?\n\nThey'll get an Agent Profile (title, phone, headshot) and a leasing-contact assignment on this property.\n\nEnter their email address:`,
      c.email ?? "",
    )
    if (email && email.trim()) {
      router.post(`/admin/properties/${property.id}/leasing_contacts/${c.id}/convert`, { email: email.trim() })
    }
  }

  const initials = (name: string) =>
    name
      .split(/\s+/)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase())
      .join("")

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Leasing Contacts — ${property.name}`} />

      <div className="space-y-6 p-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-semibold">Leasing Contacts</h1>
            <p className="text-muted-foreground text-sm">
              Treystar agents shown on every brochure for this property. Headshots appear on the Marketing brochure card.
            </p>
          </div>
          <Button render={<Link href={`/admin/properties/${property.id}/leasing_contacts/new`} />}>
            <Plus className="mr-2 h-4 w-4" /> Add Contact
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>{property.name} — Contacts ({leasing_contacts.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {leasing_contacts.length === 0 ? (
              <Empty>
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <Plus />
                  </EmptyMedia>
                  <EmptyTitle>No contacts yet</EmptyTitle>
                  <EmptyDescription>Add the leasing agent who represents this property.</EmptyDescription>
                </EmptyHeader>
              </Empty>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16">Order</TableHead>
                    <TableHead className="w-16"></TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead className="w-24"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leasing_contacts.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell className="text-muted-foreground">{c.position}</TableCell>
                      <TableCell>
                        <Avatar>
                          {c.headshot_url && <AvatarImage src={c.headshot_url} alt={c.name} />}
                          <AvatarFallback>{initials(c.name)}</AvatarFallback>
                        </Avatar>
                      </TableCell>
                      <TableCell className="font-medium">{c.name}</TableCell>
                      <TableCell>{c.title ?? "—"}</TableCell>
                      <TableCell className="text-sm">
                        {c.email && <div>{c.email}</div>}
                        {c.phone && <div className="text-muted-foreground">{c.phone}</div>}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            title="Convert to agent user"
                            onClick={() => handleConvert(c)}
                          >
                            <UserPlus className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            render={<Link href={`/admin/properties/${property.id}/leasing_contacts/${c.id}/edit`} />}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDelete(c.id)}>
                            <Trash2 className="text-destructive h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}
